package one.devsbox.www.ui.devices.edit

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import compose.assets.Svg
import compose.assets.svg.ChevronRight
import compose.assets.svg.ContentSaveOutline
import compose.assets.svg.ImageAddOutline
import compose.assets.svg.ImageOutline
import one.devsbox.shared.ads.DeviceType
import one.devsbox.www.ui.devices.label
import one.devsbox.shared.devices.ScreenRotation
import one.devsbox.shared.files.AspectRatio
import one.devsbox.shared.files.Resolutions
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.ui.widgets.*
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.log.logINFO
import tk.mallumo.utils.DataState

@Composable
@ComposableNavNode(ArgsId::class)
fun DeviceEUI() {
    with(DeviceECtx.get()) {
        ContentDeviceEUI()
    }
}

@Composable
context(ctx: DeviceECtx)
private fun ContentDeviceEUI() {
    Box(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Box(modifier = Modifier.fillMaxSize().padding(16.dp)) {
                    SuccessUI(state.nnvl)
                    ContentActions(
                        contentActions = ctx.vm.contentActions,
                        colorInactive = ctx.colorSecondaryContainer,
                        colorActive = ctx.colorPrimaryContainer
                    )
                }
            }
        }

        ctx.vm.warningMessage?.let { msg ->
            AlertDialog(
                onDismissRequest = { ctx.action.clearWarning() },
                title = { Text(stringResource(Res.string.ui_devices_edit_deviceeui_error)) },
                text = { Text(msg) },
                confirmButton = {
                    TextButton(onClick = { ctx.action.clearWarning() }) {
                        Text(stringResource(Res.string.ui_devices_edit_deviceeui_ok))
                    }
                }
            )
        }
    }
}


@Composable
context(ctx: DeviceECtx)
private fun SuccessUI(nnvl: DeviceECtx.Editor) {
    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val isNarrow = maxWidth < 1000.dp
        if (isNarrow) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                DeviceVisualSection(modifier = Modifier.fillMaxWidth().height(400.dp), nnvl = nnvl)
                IdentificationSection(nnvl = nnvl)
                TechnicalSpecsSection(nnvl = nnvl)
                Spacer(modifier = Modifier.height(250.dp))
            }
        } else {
            Row(
                modifier = Modifier.fillMaxSize(),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                DeviceVisualSection(modifier = Modifier.weight(1f).height(705.dp), nnvl = nnvl)
                Column(
                    modifier = Modifier
                        .weight(2f)
                        .verticalScroll(rememberScrollState()),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    IdentificationSection(nnvl = nnvl)
                    TechnicalSpecsSection(nnvl = nnvl)
                    Spacer(modifier = Modifier.height(250.dp))
                }
            }
        }
    }
}

@Composable
context(ctx: DeviceECtx)
private fun DeviceVisualSection(modifier: Modifier = Modifier, nnvl: DeviceECtx.Editor) {
    Column(modifier = modifier.border(1.dp, MaterialTheme.colorScheme.outlineVariant)) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            LabelSection(stringResource(Res.string.ui_devices_edit_deviceeui_label_visual))
            IconButton(onClick = { ctx.action.pickPhoto() }) {
                Icon(
                    Svg.ImageAddOutline,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onBackground,
                    modifier = Modifier.size(20.dp)
                )
            }
        }


        Box(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
                .background(MaterialTheme.colorScheme.surface)
                .clickable { ctx.action.pickPhoto() }
        ) {
            if (nnvl.photo.state > 0) {
                LazyImage(
                    id = nnvl.photo.state,
                    modifier = Modifier.fillMaxSize()
                )
            } else {
                Column(
                    modifier = Modifier.fillMaxSize(),
                    verticalArrangement = Arrangement.Center,
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Icon(
                        imageVector = Svg.ImageOutline,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                        modifier = Modifier.size(48.dp)
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                    Text(
                        text = stringResource(Res.string.ui_devices_edit_deviceeui_no_image),
                        color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f),
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }
        }
    }
}

@Composable
context(ctx: DeviceECtx)
private fun IdentificationSection(nnvl: DeviceECtx.Editor) {
    Column(
        modifier = Modifier.border(1.dp, MaterialTheme.colorScheme.outlineVariant).padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        LabelSection(stringResource(Res.string.ui_devices_edit_deviceeui_label_identification))
        DeviceRegistryNameField(nnvl)
        DeploymentAddressField(nnvl)
        LocationSelectorField(nnvl)
    }
}

@Composable
private fun DeviceRegistryNameField(nnvl: DeviceECtx.Editor) {
    Column {
        LabelField(stringResource(Res.string.ui_devices_edit_deviceeui_label_registry_name))
        OutlinedTextField(
            value = nnvl.name.text,
            onValueChange = { nnvl.name.set(it) },
            modifier = Modifier.fillMaxWidth(),
            colors = OutlinedTextFieldDefaults.colors(
                unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant,
                focusedBorderColor = MaterialTheme.colorScheme.primary
            ),
            shape = RoundedCornerShape(4.dp)
        )
    }
}

@Composable
private fun DeploymentAddressField(nnvl: DeviceECtx.Editor) {
    Column {
        LabelField(stringResource(Res.string.ui_devices_edit_deviceeui_label_address))
        OutlinedTextField(
            value = nnvl.address.text,
            onValueChange = { nnvl.address.set(it) },
            modifier = Modifier.fillMaxWidth(),
            colors = OutlinedTextFieldDefaults.colors(
                unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant,
                focusedBorderColor = MaterialTheme.colorScheme.primary
            ),
            shape = RoundedCornerShape(4.dp)
        )
    }
}

@Composable
private fun LocationSelectorField(nnvl: DeviceECtx.Editor) {
    Column {
        LabelField(stringResource(Res.string.ui_devices_edit_deviceeui_label_location))
        var showLocationDialog by remember { mutableStateOf(false) }
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .border(1.dp, MaterialTheme.colorScheme.outlineVariant, RoundedCornerShape(4.dp))
                .clickable { showLocationDialog = true }
                .padding(horizontal = 16.dp, vertical = 12.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = nnvl.nameLocation.state.ifEmpty { stringResource(Res.string.ui_devices_edit_deviceeui_select_location_placeholder) },
                    style = MaterialTheme.typography.bodyMedium,
                    color = if (nnvl.nameLocation.state.isEmpty()) MaterialTheme.colorScheme.onSurfaceVariant else MaterialTheme.colorScheme.onSurface
                )
                Icon(
                    imageVector = Svg.ChevronRight,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.size(16.dp).rotate(90f)
                )
            }
        }

        if (showLocationDialog) {
            var searchQuery by remember { mutableStateOf("") }
            val filteredLocations = remember(searchQuery, nnvl.locations) {
                if (searchQuery.isBlank()) {
                    nnvl.locations
                } else {
                    nnvl.locations.filter {
                        it.name.contains(searchQuery, ignoreCase = true) ||
                                it.address.contains(searchQuery, ignoreCase = true)
                    }
                }
            }

            AlertDialog(
                onDismissRequest = { showLocationDialog = false },
                title = {
                    Text(
                        stringResource(Res.string.ui_devices_edit_deviceeui_dialog_title_select_location),
                        color = MaterialTheme.colorScheme.onSurface
                    )
                },
                text = {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(400.dp)
                    ) {
                        OutlinedTextField(
                            value = searchQuery,
                            onValueChange = { searchQuery = it },
                            placeholder = {
                                Text(
                                    stringResource(Res.string.ui_devices_edit_deviceeui_search_location_placeholder),
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            },
                            modifier = Modifier.fillMaxWidth(),
                            colors = OutlinedTextFieldDefaults.colors(
                                unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant,
                                focusedBorderColor = MaterialTheme.colorScheme.primary,
                                focusedTextColor = MaterialTheme.colorScheme.onSurface,
                                unfocusedTextColor = MaterialTheme.colorScheme.onSurface
                            ),
                            shape = RoundedCornerShape(4.dp)
                        )

                        Spacer(modifier = Modifier.height(16.dp))

                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .weight(1f)
                                .verticalScroll(rememberScrollState())
                        ) {
                            if (filteredLocations.isEmpty()) {
                                Box(
                                    modifier = Modifier.fillMaxWidth().padding(vertical = 32.dp),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(
                                        stringResource(Res.string.ui_devices_edit_deviceeui_no_locations_found),
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }
                            } else {
                                filteredLocations.forEach { item ->
                                    Column(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .clickable {
                                                nnvl.changeLocation(item)
                                                showLocationDialog = false
                                            }
                                            .padding(vertical = 12.dp, horizontal = 8.dp)
                                    ) {
                                        Text(
                                            text = "[${item.devices}] ${item.name}",
                                            style = MaterialTheme.typography.bodyMedium,
                                            fontWeight = FontWeight.Bold,
                                            color = MaterialTheme.colorScheme.onSurface
                                        )
                                        Spacer(modifier = Modifier.height(4.dp))
                                        Text(
                                            text = item.address,
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                    }
                                    Spacer(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(1.dp)
                                            .background(MaterialTheme.colorScheme.outlineVariant)
                                    )
                                }
                            }
                        }
                    }
                },
                confirmButton = {
                    TextButton(onClick = { showLocationDialog = false }) {
                        Text(
                            stringResource(Res.string.ui_devices_edit_deviceeui_cancel),
                            color = MaterialTheme.colorScheme.primary
                        )
                    }
                },
                containerColor = MaterialTheme.colorScheme.surface
            )
        }
    }
}

@Composable
context(ctx: DeviceECtx)
private fun TechnicalSpecsSection(nnvl: DeviceECtx.Editor) {
    Column(
        modifier = Modifier.border(1.dp, MaterialTheme.colorScheme.outlineVariant).padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        LabelSection(stringResource(Res.string.ui_devices_edit_deviceeui_label_technical_specs))

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            TypeField(modifier = Modifier.weight(1f), nnvl = nnvl)
            OrientationField(modifier = Modifier.weight(1f), nnvl = nnvl)
        }

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            AspectRatioField(modifier = Modifier.weight(1f), nnvl = nnvl)
            Spacer(modifier = Modifier.weight(1f))
        }

        ResolutionModeSelector()

        when (ctx.vm.resolutionMode) {
            DeviceEVM.ResolutionMode.Picker -> ResolutionPresetField(nnvl)
            DeviceEVM.ResolutionMode.Custom -> CustomResolutionFields(nnvl)
        }
    }
}

@Composable
private fun OrientationField(modifier: Modifier = Modifier, nnvl: DeviceECtx.Editor) {
    val horizontalText = stringResource(Res.string.ui_devices_edit_deviceeui_orientation_horizontal)
    val verticalText = stringResource(Res.string.ui_devices_edit_deviceeui_orientation_vertical)
    val horizontalReversedText =
        stringResource(Res.string.ui_devices_edit_deviceeui_orientation_horizontal_reversed)
    val verticalReversedText =
        stringResource(Res.string.ui_devices_edit_deviceeui_orientation_vertical_reversed)

    Column(modifier = modifier) {
        LabelField(stringResource(Res.string.ui_devices_edit_deviceeui_label_orientation))
        SimpleDropdown(
            value = ScreenRotation[nnvl.rotation.state]!!,
            options = ScreenRotation.entries,
            label = {
                when (it) {
                    ScreenRotation.HORIZONTAL -> horizontalText
                    ScreenRotation.VERTICAL -> verticalText
                    ScreenRotation.HORIZONTAL_REVERSED -> horizontalReversedText
                    ScreenRotation.VERTICAL_REVERSED -> verticalReversedText
                }
            },
            onSelect = {
                logINFO("set rotation: $it")
                nnvl.rotation.set(it.mask)
            }
        )
    }
}

@Composable
private fun AspectRatioField(modifier: Modifier = Modifier, nnvl: DeviceECtx.Editor) {
    Column(modifier = modifier) {
        LabelField(stringResource(Res.string.ui_devices_edit_deviceeui_label_aspect_ratio))
        SimpleDropdown(
            value = nnvl.aspectRatio.state,
            options = if (nnvl.width.state < nnvl.height.state) {
                AspectRatio.vertical.map { it.label() }
            } else {
                AspectRatio.horizontal.map { it.label() }
            },
            label = { it },
            onSelect = { nnvl.aspectRatio.set(it) }
        )
    }
}

@Composable
context(ctx: DeviceECtx)
private fun ResolutionModeSelector() {
    val pickerText = stringResource(Res.string.ui_devices_edit_deviceeui_mode_picker)
    val customText = stringResource(Res.string.ui_devices_edit_deviceeui_mode_custom)

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        LabelField(stringResource(Res.string.ui_devices_edit_deviceeui_label_resolution_mode))
        Row(
            modifier = Modifier
                .background(MaterialTheme.colorScheme.surface, RoundedCornerShape(4.dp))
                .border(1.dp, MaterialTheme.colorScheme.outlineVariant, RoundedCornerShape(4.dp))
        ) {
            ModeButton(
                pickerText,
                active = ctx.vm.resolutionMode == DeviceEVM.ResolutionMode.Picker
            ) {
                ctx.vm.resolutionMode = DeviceEVM.ResolutionMode.Picker
            }
            ModeButton(
                customText,
                active = ctx.vm.resolutionMode == DeviceEVM.ResolutionMode.Custom
            ) {
                ctx.vm.resolutionMode = DeviceEVM.ResolutionMode.Custom
            }
        }
    }
}

@Composable
private fun ResolutionPresetField(nnvl: DeviceECtx.Editor) {
    Column {
        LabelField(stringResource(Res.string.ui_devices_edit_deviceeui_label_resolution_preset))
        SimpleDropdown(
            value = Resolutions.Custom(nnvl.width.state, nnvl.height.state),
            options = if (ScreenRotation[nnvl.rotation.state]!! match (ScreenRotation.HORIZONTAL + ScreenRotation.HORIZONTAL_REVERSED)) {
                Resolutions.horizontal
            } else {
                Resolutions.vertical
            },
            label = {
                buildString {
                    append("${it.w} x ${it.h}")
                    if (it !is Resolutions.Custom) {
                        append(" (${it.name})")
                    }
                }
            },
            onSelect = {
                nnvl.width.set(TextFieldValue(it.w.toString()))
                nnvl.height.set(TextFieldValue(it.h.toString()))
            }
        )
    }
}

@Composable
private fun CustomResolutionFields(nnvl: DeviceECtx.Editor) {
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
        Column(modifier = Modifier.weight(1f)) {
            LabelField(stringResource(Res.string.ui_devices_edit_deviceeui_label_width))
            OutlinedTextField(
                value = nnvl.width.text,
                onValueChange = { nnvl.width.set(it) },
                modifier = Modifier.fillMaxWidth(),
                isError = nnvl.width.err,
                colors = OutlinedTextFieldDefaults.colors(
                    unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant,
                    focusedBorderColor = MaterialTheme.colorScheme.primary
                ),
                shape = RoundedCornerShape(4.dp)
            )
        }
        Column(modifier = Modifier.weight(1f)) {
            LabelField(stringResource(Res.string.ui_devices_edit_deviceeui_label_height))
            OutlinedTextField(
                value = nnvl.height.text,
                onValueChange = { nnvl.height.set(it) },
                modifier = Modifier.fillMaxWidth(),
                isError = nnvl.height.err,
                colors = OutlinedTextFieldDefaults.colors(
                    unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant,
                    focusedBorderColor = MaterialTheme.colorScheme.primary
                ),
                shape = RoundedCornerShape(4.dp)
            )
        }
    }
}


@Composable
private fun ModeButton(text: String, active: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .background(
                if (active) MaterialTheme.colorScheme.primaryContainer else Color.Transparent,
                RoundedCornerShape(4.dp)
            )
            .clickable { onClick() }
            .padding(horizontal = 16.dp, vertical = 8.dp),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = text,
            style = MaterialTheme.typography.labelMedium,
            fontWeight = FontWeight.Bold,
            color = if (active) MaterialTheme.colorScheme.onPrimaryContainer else MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}

@Composable
private fun <T> SimpleDropdown(
    value: T,
    options: List<T>,
    label: @Composable (T) -> String,
    onSelect: (T) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .border(1.dp, MaterialTheme.colorScheme.outlineVariant, RoundedCornerShape(4.dp))
            .clickable { expanded = true }
            .padding(horizontal = 16.dp, vertical = 12.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                label(value),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurface
            )
            Icon(
                imageVector = Svg.ChevronRight,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.size(16.dp).rotate(90f)
            )
        }

        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            modifier = Modifier.background(MaterialTheme.colorScheme.surface)
        ) {
            options.forEach { option ->
                DropdownMenuItem(
                    text = { Text(label(option), color = MaterialTheme.colorScheme.onSurface) },
                    onClick = {
                        onSelect(option)
                        expanded = false
                    }
                )
            }
        }
    }
}

@Composable
private fun TypeField(modifier: Modifier = Modifier, nnvl: DeviceECtx.Editor) {
    Column(modifier = modifier) {
        LabelField(stringResource(Res.string.ui_devices_detail_devicedui_label_type))
        SimpleDropdown(
            value = DeviceType[nnvl.deviceType.state] ?: DeviceType.INDOOR,
            options = DeviceType.entries,
            label = { stringResource(it.label()) },
            onSelect = { nnvl.deviceType.set(it.mask) }
        )
    }
}
